import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  getBetaOverview,
  listBetaParticipants,
  getBetaAttention,
  getBetaMetrics,
} from "@/lib/beta.functions";
import { PageHeader } from "@/components/admin/AdminPrimitives";
import { AlertTriangle, Users, CheckCircle2, Activity } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/beta/")({
  head: () => ({
    meta: [
      { title: "Beta — Operação" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: BetaOperationsPage,
});

function BetaOperationsPage() {
  const overviewFn = useServerFn(getBetaOverview);
  const listFn = useServerFn(listBetaParticipants);
  const attentionFn = useServerFn(getBetaAttention);
  const metricsFn = useServerFn(getBetaMetrics);

  const [statusFilter, setStatusFilter] = useState<string>("");
  const [activityFilter, setActivityFilter] = useState<"todos" | "ativo" | "inativo">("todos");

  const ov = useQuery({ queryKey: ["beta-overview"], queryFn: () => overviewFn() });
  const list = useQuery({
    queryKey: ["beta-list", statusFilter, activityFilter],
    queryFn: () =>
      listFn({
        data: {
          status: statusFilter || undefined,
          activityLevel: activityFilter,
        },
      }),
  });
  const at = useQuery({ queryKey: ["beta-attention"], queryFn: () => attentionFn() });
  const mt = useQuery({ queryKey: ["beta-metrics"], queryFn: () => metricsFn() });

  const kpis = useMemo(
    () => [
      { label: "Participantes", value: ov.data?.participantsTotal ?? "—", icon: Users },
      { label: "Cadastrados", value: ov.data?.cadastrados ?? "—" },
      { label: "Assinaturas ativas", value: ov.data?.assinaturasAtivas ?? "—", icon: CheckCircle2 },
      { label: "Projetos criados", value: ov.data?.projetosCriados ?? "—" },
      { label: "Primeira tarefa", value: ov.data?.primeiraTarefa ?? "—" },
      { label: "Ativos 7d", value: ov.data?.ativos7d ?? "—", icon: Activity },
      { label: "Inativos 7d+", value: ov.data?.inativos7d ?? "—" },
      { label: "Com erro", value: ov.data?.comErro ?? "—", icon: AlertTriangle },
      { label: "Com feedback", value: ov.data?.comFeedback ?? "—" },
    ],
    [ov.data],
  );

  return (
    <div className="space-y-8">
      <PageHeader
        title="Operação do Beta"
        description="Acompanhamento diário do beta controlado — do convite à primeira entrega de valor."
      />

      <section className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
        {kpis.map((k) => (
          <div
            key={k.label}
            className="rounded-xl border border-black/[0.06] bg-white p-4"
          >
            <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.14em] text-[var(--gray-ink)]/70">
              <span>{k.label}</span>
              {"icon" in k && k.icon ? <k.icon size={14} /> : null}
            </div>
            <div className="mt-1 text-2xl font-semibold text-[var(--navy)]">{String(k.value)}</div>
          </div>
        ))}
      </section>

      {mt.data && (
        <section className="rounded-xl border border-black/[0.06] bg-white p-5">
          <h2 className="text-sm font-semibold text-[var(--navy)]">Métricas do beta</h2>
          <div className="mt-4 grid gap-4 md:grid-cols-3">
            <MetricGroup title="Ativação">
              <MetricRow label="Cadastro → assinatura" value={`${mt.data.activation.cadastro_para_assinatura}%`} />
              <MetricRow label="Assinatura → projeto" value={`${mt.data.activation.assinatura_para_projeto}%`} />
              <MetricRow label="Projeto → 1ª tarefa" value={`${mt.data.activation.projeto_para_primeira_tarefa}%`} />
              <MetricRow
                label="Tempo médio até 1ª tarefa"
                value={mt.data.activation.tempo_medio_ate_primeira_tarefa_horas != null ? `${mt.data.activation.tempo_medio_ate_primeira_tarefa_horas}h` : "—"}
              />
            </MetricGroup>
            <MetricGroup title="Engajamento">
              <MetricRow label="Progresso médio" value={`${mt.data.engagement.progresso_medio}%`} />
              <MetricRow label="Tarefas por usuário" value={mt.data.engagement.tarefas_por_usuario} />
              <MetricRow label="Missões concluídas" value={mt.data.engagement.missoes_concluidas} />
              <MetricRow label="Tarefas totais" value={mt.data.engagement.tarefas_totais} />
            </MetricGroup>
            <MetricGroup title="Qualidade">
              <MetricRow label="Feedbacks totais" value={mt.data.quality.feedbacks_totais} />
              <MetricRow label="Feedbacks negativos" value={mt.data.quality.feedbacks_negativos} />
              <MetricRow label="Assinaturas ativas" value={mt.data.quality.assinaturas_ativas} />
            </MetricGroup>
          </div>
        </section>
      )}

      <section className="rounded-xl border border-black/[0.06] bg-white">
        <header className="flex items-center justify-between border-b border-black/5 px-5 py-3">
          <h2 className="text-sm font-semibold text-[var(--navy)]">Requer atenção</h2>
          <span className="text-xs text-[var(--gray-ink)]">
            {at.data?.alerts.length ?? 0} alertas
          </span>
        </header>
        <div className="max-h-80 overflow-auto">
          {(at.data?.alerts ?? []).length === 0 ? (
            <div className="px-5 py-6 text-sm text-[var(--gray-ink)]">
              Nenhum alerta operacional no momento.
            </div>
          ) : (
            <ul className="divide-y divide-black/5">
              {at.data!.alerts.map((a: any, i: number) => (
                <li key={i} className="flex items-center justify-between px-5 py-3">
                  <div>
                    <div className="text-sm font-medium">
                      {a.email ?? a.user_id.slice(0, 8)} —{" "}
                      <span className="text-[var(--gray-ink)]">{a.label}</span>
                    </div>
                    <div className="text-[11px] text-[var(--gray-ink)]/70">
                      desde {new Date(a.since).toLocaleString("pt-BR")}
                    </div>
                  </div>
                  <Link
                    to="/admin/beta/$userId"
                    params={{ userId: a.user_id }}
                    className="text-xs font-semibold text-[var(--navy)] hover:underline"
                  >
                    Abrir
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <section className="rounded-xl border border-black/[0.06] bg-white">
        <header className="flex flex-wrap items-center gap-3 border-b border-black/5 px-5 py-3">
          <h2 className="text-sm font-semibold text-[var(--navy)]">Participantes</h2>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-md border border-black/10 px-2 py-1 text-xs"
          >
            <option value="">Toda situação</option>
            <option value="convidado">Convidado</option>
            <option value="cadastrado">Cadastrado</option>
            <option value="assinatura_pendente">Assinatura pendente</option>
            <option value="ativado">Ativação concluída</option>
            <option value="em_andamento">Em andamento</option>
            <option value="inativo">Inativo</option>
            <option value="concluido">Concluído</option>
            <option value="requer_atencao">Requer atenção</option>
          </select>
          <select
            value={activityFilter}
            onChange={(e) => setActivityFilter(e.target.value as any)}
            className="rounded-md border border-black/10 px-2 py-1 text-xs"
          >
            <option value="todos">Toda atividade</option>
            <option value="ativo">Ativo 7d</option>
            <option value="inativo">Inativo 7d</option>
          </select>
          <span className="ml-auto text-xs text-[var(--gray-ink)]">
            {list.data?.items.length ?? 0} registros
          </span>
        </header>
        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-[10px] uppercase tracking-[0.14em] text-[var(--gray-ink)]/70">
              <tr>
                <th className="px-4 py-3">Usuário</th>
                <th className="px-4 py-3">Coorte</th>
                <th className="px-4 py-3">Assinatura</th>
                <th className="px-4 py-3">Progresso</th>
                <th className="px-4 py-3">1ª tarefa</th>
                <th className="px-4 py-3">Última atividade</th>
                <th className="px-4 py-3">Situação</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {(list.data?.items ?? []).map((p: any) => (
                <tr key={p.user_id} className="border-t border-black/5">
                  <td className="px-4 py-3">
                    <div className="font-medium">{p.full_name ?? p.email ?? p.user_id.slice(0, 8)}</div>
                    <div className="text-[11px] text-[var(--gray-ink)]/70">{p.email}</div>
                  </td>
                  <td className="px-4 py-3 text-xs">{p.cohort}</td>
                  <td className="px-4 py-3 text-xs">{p.subscription}</td>
                  <td className="px-4 py-3 text-xs">
                    {p.progress_pct != null ? `${Number(p.progress_pct).toFixed(0)}%` : "—"}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {p.first_task_at
                      ? new Date(p.first_task_at).toLocaleDateString("pt-BR")
                      : "—"}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {p.last_activity_at
                      ? new Date(p.last_activity_at).toLocaleDateString("pt-BR")
                      : "—"}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                        p.has_recent_error
                          ? "bg-red-50 text-red-700"
                          : p.active_last_7d
                            ? "bg-emerald-50 text-emerald-700"
                            : "bg-[var(--gray-soft)] text-[var(--gray-ink)]"
                      }`}
                    >
                      {p.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link
                      to="/admin/beta/$userId"
                      params={{ userId: p.user_id }}
                      className="text-xs font-semibold text-[var(--navy)] hover:underline"
                    >
                      Abrir
                    </Link>
                  </td>
                </tr>
              ))}
              {list.data?.items.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-sm text-[var(--gray-ink)]">
                    Nenhum participante registrado. Adicione manualmente pela API ou aguarde o primeiro cadastro do beta.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function MetricGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-black/5 bg-[var(--gray-soft)]/40 p-4">
      <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--gray-ink)]/70">
        {title}
      </div>
      <div className="mt-2 space-y-1.5">{children}</div>
    </div>
  );
}
function MetricRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-[var(--gray-ink)]">{label}</span>
      <span className="font-semibold text-[var(--navy)]">{value}</span>
    </div>
  );
}
